import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import InlineNotice, { InlineNoticeProps } from './InlineNotice';

export type NoticeInput = Omit<InlineNoticeProps, 'onClose'> & { id?: string };
export type Notice = NoticeInput & { id: string };

type NoticeContextType = {
  show: (n: NoticeInput) => string; // returns id
  dismiss: (id: string) => void;
  clear: () => void;
};

const NoticeContext = createContext<NoticeContextType | null>(null);

export const useNotice = (): NoticeContextType => {
  const ctx = useContext(NoticeContext);
  if (!ctx) throw new Error('useNotice must be used within NoticeProvider');
  return ctx;
};

export type NoticePosition = 'top-right' | 'top-left' | 'top-center' | 'bottom-right' | 'bottom-left' | 'bottom-center';

export const NoticeProvider: React.FC<{ children: React.ReactNode; position?: NoticePosition }> = ({ children, position = 'bottom-center' }) => {
  const [items, setItems] = useState<Notice[]>([]);

  const show = useCallback((n: NoticeInput) => {
    const id = n.id || Math.random().toString(36).slice(2);
    setItems(prev => [...prev, { ...n, id }]);
    return id;
  }, []);

  const dismiss = useCallback((id: string) => {
    setItems(prev => prev.filter(n => n.id !== id));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const value = useMemo(() => ({ show, dismiss, clear }), [show, dismiss, clear]);

  const containerClass = useMemo(() => {
    const base = 'fixed z-[9999] space-y-2 w-[360px] max-w-[88vw]';
    switch (position) {
      case 'top-right':
        return `${base} top-4 right-4`;
      case 'top-left':
        return `${base} top-4 left-4`;
      case 'top-center':
        return `${base} top-4 left-1/2 -translate-x-1/2`;
      case 'bottom-right':
        return `${base} bottom-4 right-4`;
      case 'bottom-left':
        return `${base} bottom-4 left-4`;
      case 'bottom-center':
      default:
        return `${base} bottom-4 left-1/2 -translate-x-1/2`;
    }
  }, [position]);

  return (
    <NoticeContext.Provider value={value}>
      {children}
      {/* Global notice stack */}
      <div className={containerClass}>
        {items.map(n => (
          <InlineNotice
            key={n.id}
            type={n.type}
            message={n.message}
            details={n.details}
            onClose={() => dismiss(n.id)}
            autoDismissMs={n.autoDismissMs}
          />
        ))}
      </div>
    </NoticeContext.Provider>
  );
};

export default NoticeProvider;
